import React, { useEffect, useState, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { useIntl, FormattedDate } from "react-intl";
import { X, Info, FileText } from "react-feather";
import {
  getResults,
  getAreaInfo,
  generateResultPdf,
  getResultsMapLayers,
  exportCoordinates,
  getCarLayers
} from "../../api";
import { formatCityName } from "../../utils/format";
import AreaMapResult from "../../components/AreaMapResult";
import Skeleton from "../../components/Skeleton";
import UserContext from "../../context/UserContext";
import DocumentCriteria from "./components/DocumentCriteria";
import AreaCriteria from "./components/AreaCriteria";
import CriteriaList from "./components/CriteriaList";
import "./styles.scss";
import { CSVLink } from "react-csv";

const Results = ({ setError }) => {
  const { id } = useParams();
  // const area_id = useState(null);
  const { formatMessage, formatNumber } = useIntl();
  const [results, setResults] = useState(null);
  const [areaInfo, setAreaInfo] = useState(null);
  const [selectedCriteria, setSelectedCriteria] = useState([]);
  const [openCriteria, setOpenCriteria] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const userData = useContext(UserContext);
  const [selectedTemplate, setSelectedTemplate] = useState("all");
  const [csvReport, setCsvReport] = useState([]);

  const fetchResults = useCallback(() => {
    getResults(id)
      .then(res => {
        const results = { ...res.data };
        const documentCriteria = {};

        res.data.B3ConsultaCriterioPessoa.forEach(crit => {
          if (!documentCriteria.hasOwnProperty(crit.B3Criterio.id)) {
            documentCriteria[crit.B3Criterio.id] = {
              B3Criterio: crit.B3Criterio,
              B3CriterioVersao: crit.B3CriterioVersao,
              data_consulta:
                crit.detalhes_json && crit.detalhes_json.data_consulta,
              documents: {
                has_infractions: [],
                clean: []
              }
            };
          } else if (
            !documentCriteria[crit.B3Criterio.id].data_consulta &&
            crit.detalhes_json
          ) {
            documentCriteria[crit.B3Criterio.id].data_consulta =
              crit.detalhes_json.data_consulta;
          }

          documentCriteria[crit.B3Criterio.id].documents[
            crit.conforme ? "clean" : "has_infractions"
          ].push(crit);
        });

        results.documentCriteria = Object.values(documentCriteria);

        setResults(results);

        const consultaAreaId = results.B3Consulta.requisicao_json.area_id;
        if (consultaAreaId) {
          exportCoordinates(results.B3Consulta.requisicao_json.area_id)
            .then(response => {
              const csvReport = response.data.result.coordenadas;
              setCsvReport(csvReport);
              // console.log("csvReport2", csvReport);
            })
            .catch(err => {
              setPdfError(
                err?.response?.data?.message ||
                formatMessage({ id: "erro-api" })
              );
            });
        }
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  }, [id, formatMessage, setError]);

  const toggleCollapse = useCallback(id => {
    setOpenCriteria(openCriteria =>
      openCriteria.includes(id)
        ? openCriteria.filter(crit => crit !== id)
        : [...openCriteria, id]
    );
  }, []);

  const toggleCollapseAll = useCallback(() => {
    setOpenCriteria(openCriteria =>
      openCriteria.length <
        results.B3ConsultaCriterioArea.length + results.documentCriteria.length
        ? [
          ...results.B3ConsultaCriterioArea.map(criterion => criterion.id),
          ...results.documentCriteria.map(
            criterion => criterion.B3Criterio.id
          )
        ]
        : []
    );
  }, [results]);

  const generatePdf = useCallback(() => {
    setPdfLoading(true);
    generateResultPdf(id, selectedCriteria, selectedTemplate)
      .then(response => {
        window.open(response.data.url_pdf, "_self");
      })
      .catch(err => {
        setPdfError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      })
      .finally(() => {
        setPdfLoading(false);
      });
  }, [id, formatMessage, selectedCriteria, selectedTemplate]);

  useEffect(() => {
    userData && userData.updateBalance();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    if (results) {
      if (
        !!results.B3ConsultaCriterioArea.find(
          crit => crit.status !== "processado"
        ) ||
        !!results.B3ConsultaCriterioPessoa.find(
          crit => crit.status !== "processado"
        )
      ) {
        const refetch = setTimeout(fetchResults, 15000);
        setProcessing(true);

        return () => clearTimeout(refetch);
      } else {
        setProcessing(false);
      }
    }
  }, [results, fetchResults]);

  useEffect(() => {
    if (results?.B3Consulta?.requisicao_json?.area_id) {
      Promise.allSettled([
        getAreaInfo(results.B3Consulta.requisicao_json.area_id),
        getResultsMapLayers(id),
        getCarLayers(id)
      ]).then(responses => {
        const errorMessages = responses
          .filter(res => res.status === "rejected")
          .map(res => res.reason.response.data.message);

        if (errorMessages.length > 0) {
          setError(
            errorMessages[0] ||
            errorMessages[1] ||
            formatMessage({ id: "erro-api" })
          );
        } else {
          setAreaInfo({
            area: responses[0].value.data.result,
            layers: responses[1].value.data.result,
            car_layers: responses[2].value.data.result
          });
        }
      });
    }
  }, [id, results, setAreaInfo, formatMessage, setError]);

  return (
    <>
      <h1>{formatMessage({ id: "resultado-consulta" })}</h1>

      <div className="content-box results">
        {processing && (
          <div className="processing-message">
            <Info size="14px" />{" "}
            {formatMessage({ id: "mensagem-processando-consulta" })}
          </div>
        )}

        <div className="tile is-ancestor">
          <div className="report-overview tile is-vertical is-4">
            <p>
              <span className="bold">
                {formatMessage({ id: "nome-consulta" })}:
              </span>{" "}
              {results ? (
                results.B3Consulta.nome || (
                  <span className="untitled">
                    {formatMessage({ id: "sem-titulo" })}
                  </span>
                )
              ) : (
                <Skeleton width="200" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "codigo-consulta" })}:
              </span>{" "}
              {results ? results.B3Consulta?.trackid : <Skeleton width="60" />}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "data-consulta" })}:
              </span>{" "}
              {results ? (
                <FormattedDate
                  value={new Date(results.B3Consulta.data_consulta)}
                />
              ) : (
                <Skeleton width="100" />
              )}
            </p>
            <p>
              <span className="bold">
                {formatMessage({ id: "ultima-atualizacao" })}:
              </span>{" "}
              {results ? (
                <FormattedDate value={new Date(results.B3Consulta.modified)} />
              ) : (
                <Skeleton width="100" />
              )}
            </p>

            {areaInfo?.area && (
              <>
                {areaInfo.area.Municipio && (
                  <p>
                    <span className="bold">
                      {formatMessage({ id: "municipio" })}:
                    </span>{" "}
                    {formatCityName(areaInfo.area.Municipio)}
                  </p>
                )}
                {areaInfo.area.B3Area && areaInfo.area.B3Area.area_ha && (
                  <p>
                    <span className="bold">
                      {formatMessage({ id: "area" })}:
                    </span>
                    {` ${formatNumber(areaInfo.area.B3Area.area_ha)} ha`}
                  </p>
                )}
              </>
            )}

            {results?.B3Consulta?.requisicao_json?.proponente && (
              <>
                <h5>
                  {formatMessage(
                    { id: "Documento" },
                    {
                      num: results.B3Consulta.requisicao_json.proponente.length
                    }
                  )}
                  :
                </h5>
                {results.B3Consulta.requisicao_json.proponente.map(document => (
                  <li key={document.label}>{document.label}</li>
                ))}
              </>
            )}

            <p>
              <button
                className={`button secondary export-pdf is-small${pdfLoading ? " is-loading" : ""
                  }`}
                onClick={generatePdf}
              >
                <FileText />
                {formatMessage({ id: "exportar-como-pdf" })}
              </button>
            </p>

            <p>
              <button className={`button secondary export-pdf is-small`}>
                <FileText />
                <CSVLink
                  data={csvReport}
                  filename={"coordenadas.csv"}
                  style={{ color: "#fff" }}
                >
                  {formatMessage({ id: "exportar-coordenadas" })}
                </CSVLink>
              </button>
            </p>

            {pdfError && (
              <p className="pdf-error-message">
                <X />
                {pdfError}
              </p>
            )}
          </div>
          <div className="area-info tile is-vertical is-8">
            {areaInfo && (
              <AreaMapResult
                aoiInfo={areaInfo.area}
                layers={areaInfo.layers}
                car_layers={areaInfo.car_layers}
                className="area-map tile is-vertical"
                showLegend
              />
            )}
          </div>
        </div>

        <h2>
          {formatMessage({
            id: "criterios"
          })}

          {results ? (
            <button
              className="button secondary is-small collapse-all"
              onClick={toggleCollapseAll}
            >
              {formatMessage({
                id:
                  openCriteria.length <
                    results.B3ConsultaCriterioArea.length +
                    results.documentCriteria.length
                    ? "expandir-todos"
                    : "ocultar-todos"
              })}
            </button>
          ) : (
            <Skeleton width={80} ml={20} />
          )}
        </h2>

        <div className="tile is-ancestor">
          <div className="tile is-vertical is-4">
            <CriteriaList
              loading={!!results}
              areaCriteria={results?.B3ConsultaCriterioArea}
              documentCriteria={results?.documentCriteria}
              selectedCriteria={selectedCriteria}
              setSelectedCriteria={setSelectedCriteria}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
            />
          </div>

          <div className="tile is-vertical is-8">
            {!results && (
              <>
                <h3>
                  <Skeleton width="120" mb="-5" />
                </h3>
                <Skeleton />
                <Skeleton />

                <h3>
                  <Skeleton width="200" />
                </h3>
                <Skeleton />
                <Skeleton />
              </>
            )}

            {results && results.B3ConsultaCriterioArea.length > 0 && (
              <AreaCriteria
                criteria={results.B3ConsultaCriterioArea}
                selectedCriteria={selectedCriteria}
                openCriteria={openCriteria}
                toggleCollapse={toggleCollapse}
              />
            )}

            {results && results.B3ConsultaCriterioPessoa.length > 0 && (
              <DocumentCriteria
                criteria={results.documentCriteria}
                selectedCriteria={selectedCriteria}
                openCriteria={openCriteria}
                toggleCollapse={toggleCollapse}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Results;
